import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, ValidatorFn, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ReviewDetailTabsService } from 'review-detail-tabs';
import { TaskBasedLaborSettings } from '../../../../models/dtos';
import { FireEstimateAnalysisType } from '../../../../models/enum';
import { CONSTANTS } from '../../../../Constants/Constants';
import { IJobDetailsFormGroup } from '../../../../models/properties/IJobDetailsFormGroup';
import { NumericFieldConfig } from '../../../../shared/numeric/numeric.types';
import { NumericMaskDirective } from '../../../../shared/numeric/numeric-mask.directive';
import { TrackChangesDirective } from '../../../../directives/track-changes.directive';
import { CommonDataService } from '../../../../services/common-data.service';
import { CommonFunctionsService } from '../../../../services/common-functions.service';

type ProductOfferingControlName =
  | 'fireAlarmOffering'
  | 'fireSprinklerOffering'
  | 'gaseousSystemOffering'
  | 'ercesOffering';

type NumericControlName = 'siteSize' | 'antennaQty' | 'agentWeight' | 'cleanAgentNetVolSpace';


@Component({
  selector: 'app-job-details',
  imports: [
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    ReactiveFormsModule,
    CommonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatDialogModule,
    NumericMaskDirective,
    TrackChangesDirective
  ],
  templateUrl: './job-details.component.html',
  styleUrl: './job-details.component.scss'
})
export class JobDetails implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  productOfferingOptions: {label: FireEstimateAnalysisType, controlName: ProductOfferingControlName}[] = []
  installationTypeOptions: string[] = [];
  maxNumericControlValue = CONSTANTS.MAX_JOB_DETAILS_NUMERIC_VALUE;

  jobDetailsForm!: FormGroup<IJobDetailsFormGroup>;

  readonly numericFieldsConfig: NumericFieldConfig = {
    kind: 'integer',
    scale: CONSTANTS.ZERO,
    min: CONSTANTS.ZERO,
    max: CONSTANTS.MAX_JOB_DETAILS_NUMERIC_VALUE,
    useGrouping: false,
    showStepper: false,
    step: CONSTANTS.ONE,
    revertToLastValidValue: true,
  }

  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly reviewDetailTabsService: ReviewDetailTabsService,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly dialogRef: MatDialogRef<JobDetails>
  ) {}

  ngOnInit(): void {
    this.setDropdownValues();
    this.initializeJobDetailsForm();
    this.setStateOfInputFields();

    this.productOfferingOptions = [
      { label: FireEstimateAnalysisType.FIRE_ALARM, controlName: 'fireAlarmOffering' },
      { label: FireEstimateAnalysisType.FIRE_SPRINKLER, controlName: 'fireSprinklerOffering' },
      { label: FireEstimateAnalysisType.GASEOUS_SYSTEM, controlName: 'gaseousSystemOffering' },
      { label: FireEstimateAnalysisType.ERCES, controlName: 'ercesOffering' }
    ];

    this.productOfferingOptions.forEach(({ controlName }) => {
      this.jobDetailsForm.controls[controlName].valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.setStateOfInputFields());
    });
  }

  private setDropdownValues(): void {
    this.installationTypeOptions = this.commonFunctionsService.getFireJobDetailsProductTypes();
  }

  initializeJobDetailsForm(): void {
    const taskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();

    this.jobDetailsForm = this.fb.group<IJobDetailsFormGroup>({
      fireAlarmOffering: this.fb.control(taskBasedLaborSettings?.isFireAlarmOfferingSelected ?? false),
      fireSprinklerOffering: this.fb.control(taskBasedLaborSettings?.isFireSprinklerOfferingSelected ?? false),
      gaseousSystemOffering: this.fb.control(taskBasedLaborSettings?.isGasSystemOfferingSelected ?? false),
      ercesOffering: this.fb.control(taskBasedLaborSettings?.isERCESOfferingSelected ?? false),
      installationType: this.fb.control(taskBasedLaborSettings?.selectedProductTypeValue || this.installationTypeOptions[0] || CONSTANTS.EMPTY_STRING),
      siteSize: this.fb.control(taskBasedLaborSettings?.fireJobDetailsSiteSize ?? CONSTANTS.ZERO, this.validateNumericInput()),
      antennaQty: this.fb.control(taskBasedLaborSettings?.fireJobDetailsAntennaQty ?? CONSTANTS.ZERO, this.validateNumericInput()),
      agentWeight: this.fb.control(taskBasedLaborSettings?.fireJobDetailsAgentWeight ?? CONSTANTS.ZERO, this.validateNumericInput()),
      cleanAgentNetVolSpace: this.fb.control(taskBasedLaborSettings?.fireJobDetailsCleanAgentVolSpace ?? CONSTANTS.ZERO, this.validateNumericInput()),
    });
  }

  setStateOfInputFields(): void {
    const formValues = this.jobDetailsForm.getRawValue();
    const states = this.reviewDetailTabsService.setStateOfJobDetailsInputFields(
      formValues.fireSprinklerOffering,
      formValues.fireAlarmOffering,
      formValues.gaseousSystemOffering,
      formValues.ercesOffering
    );

    this.setControlState(states.isSiteSizeQtyEnabled, 'siteSize');
    this.setControlState(states.isAntennaQtyEnabled, 'antennaQty');
    this.setControlState(states.isAgentWeightEnabled, 'agentWeight');
    this.setControlState(states.isCleanAgentNetVolEnabled, 'cleanAgentNetVolSpace');
  }
  private setControlState(isEnabled: boolean, controlName: NumericControlName): void {
    const control = this.jobDetailsForm.get(controlName);
    if (!control) {
      return;
    }

    if (isEnabled) {
      control.enable({ emitEvent: false });
      return;
    }

    control.disable({ emitEvent: false });
  }

    onOkay(): void {
    const formValues = this.jobDetailsForm.getRawValue();
    const currentTaskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();

    if (!currentTaskBasedLaborSettings) {
      return;
    }

    const updatedTaskBasedLaborSettings: TaskBasedLaborSettings = {
      ...currentTaskBasedLaborSettings,
      isFireAlarmOfferingSelected: formValues.fireAlarmOffering,
      isFireSprinklerOfferingSelected: formValues.fireSprinklerOffering,
      isGasSystemOfferingSelected: formValues.gaseousSystemOffering,
      isERCESOfferingSelected: formValues.ercesOffering,
      selectedProductTypeValue: formValues.installationType,
      fireJobDetailsSiteSize: formValues.siteSize,
      fireJobDetailsAntennaQty: formValues.antennaQty,
      fireJobDetailsAgentWeight: formValues.agentWeight,
      fireJobDetailsCleanAgentVolSpace: formValues.cleanAgentNetVolSpace,
    };

    const summary = this.commonDataService.getCostReviewData()?.reviewObject?.costReviewSummary;
    if (summary) {
      this.commonFunctionsService.mapJobDetailsToSummary(summary, updatedTaskBasedLaborSettings);
    }

    this.commonDataService.setTaskBasedLaborSettings(updatedTaskBasedLaborSettings);
    this.dialogRef.close();
  }

  private validateNumericInput(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value= control.value;
      if (this.isInvalidNumericValue(value) || value < CONSTANTS.ZERO || !Number.isInteger(value) || value > this.maxNumericControlValue) {
        return { invalid: true };
      }
      return null;
    };
  }
  private isInvalidNumericValue(value: number): boolean {
    if (value === null || value === undefined || Number.isNaN(value)){
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
